import { useCallback, useEffect, useMemo, useState } from "react";
import { loadSlab } from "./finish";
import { plywoodDataUrl } from "../components/plywood";

// The look of the DJ Booth controller (pages/DjBooth.js, painted by components/djbooth.css). A person picks one of the finishes below
// from a pull-down, or "My colors" and chooses their own Base, Knobs, Switches and Lights. The choice is kept on the device (like the
// site's other looks). The DJ Booth is for Premium members, so no look is locked.
//   Base     = the body of the controller (the panels the decks, the mixer and the song list sit on)
//   Knobs    = the round and square caps of the sliders
//   Switches = every button (play, deck numbers, fade, tabs...)
//   Lights   = the glow of lit things (progress bars, a playing deck, a pressed switch). In the finishes each deck keeps its own
//              light color; in "My colors" one color lights everything.
export const LOOKS = [
  ["steel", "Steel"], ["wood", "Wood"], ["mint", "Mint"], ["cherry", "Cherry"], ["space", "Space age"], ["gold", "Gold"], ["quartz", "Quartz"],
  ["mine", "My colors"],
];
export const MINE_PARTS = [["base", "Base"], ["knob", "Knobs"], ["sw", "Switches"], ["lit", "Lights"]];
export const MINE_DEFAULT = { base: "#2f3a4a", knob: "#e9eef5", sw: "#d9a21b", lit: "#22d3ee" };

const LOOK_KEY = "dtm_dj_look";
const MINE_KEY = "dtm_dj_mine";

const HEX = /^#[0-9a-fA-F]{6}$/;
export function readLook() {
  try {
    const v = localStorage.getItem(LOOK_KEY);
    return LOOKS.some(([k]) => k === v) ? v : "steel";
  } catch { return "steel"; }
}
export function readMine() {
  try {
    const o = JSON.parse(localStorage.getItem(MINE_KEY) || "null") || {};
    const out = { ...MINE_DEFAULT };
    Object.keys(out).forEach((k) => { if (typeof o[k] === "string" && HEX.test(o[k])) out[k] = o[k].toLowerCase(); });
    return out;
  } catch { return { ...MINE_DEFAULT }; }
}
function keep(key, value) {
  try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value); } catch { /* storage blocked: lasts until the page is reloaded */ }
}

// ---- small color helpers (for "My colors": a lighter and darker shade of what was picked, and dark or light writing on top) ----
const rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const hex2 = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
export const mix = (a, b, t) => {
  const x = rgb(a); const y = rgb(b);
  return `#${[0, 1, 2].map((i) => hex2(x[i] + (y[i] - x[i]) * t)).join("")}`;
};
export const isLight = (h) => {
  const [r, g, b] = rgb(h).map((v) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.4;
};

// ---- the finishes ----
// every look gives the same set of CSS variables
const DARK_CARD = "rgba(0,0,0,0.26)";
const LIGHT_CARD = "rgba(255,255,255,0.42)";
const PRESETS = {
  steel: {
    "--dj-base": "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, rgba(0,0,0,0.05) 1px 3px), linear-gradient(160deg, #5a6572 0%, #3d4651 42%, #262c34 100%)",
    "--dj-card": DARK_CARD, "--dj-field": "rgba(0,0,0,0.35)", "--dj-groove": "rgba(0,0,0,0.55)",
    "--dj-edge": "#11161b", "--dj-ink": "#eef3f8", "--dj-dim": "#aab7c4",
    "--dj-knob-hi": "#ffffff", "--dj-knob-1": "#dfe6ee", "--dj-knob-2": "#8693a1", "--dj-knob-rim": "#0f1317",
    "--dj-sw": "radial-gradient(ellipse 60% 40% at 38% 22%, rgba(255,255,255,0.85), rgba(255,255,255,0) 100%), linear-gradient(180deg, #e3e9f0 0%, #aab5c1 52%, #7d8896 100%)",
    "--dj-sw-ink": "#0d1217", "--dj-sw-rim": "#0f1317", "--dj-lit": "#22d3ee",
  },
  wood: {
    "--dj-base": "linear-gradient(rgba(0,0,0,0.10), rgba(0,0,0,0.30)), var(--dj-wood, linear-gradient(160deg, #9a6638, #6b4222))",
    "--dj-card": "rgba(30,14,4,0.38)", "--dj-field": "rgba(25,10,2,0.45)", "--dj-groove": "rgba(20,8,0,0.6)",
    "--dj-edge": "#2a1608", "--dj-ink": "#fbefd9", "--dj-dim": "#dcc3a0",
    "--dj-knob-hi": "#fff3cf", "--dj-knob-1": "#e6c27a", "--dj-knob-2": "#8c6a22", "--dj-knob-rim": "#2a1608",
    "--dj-sw": "radial-gradient(ellipse 60% 40% at 38% 22%, rgba(255,235,200,0.35), rgba(255,235,200,0) 100%), linear-gradient(180deg, #5b3a1f 0%, #3d2410 55%, #26150a 100%)",
    "--dj-sw-ink": "#fbefd9", "--dj-sw-rim": "#1a0d04", "--dj-lit": "#f6b24a",
  },
  mint: {
    "--dj-base": "linear-gradient(160deg, #b9ecda 0%, #8fd1bd 45%, #6db79f 100%)",
    "--dj-card": LIGHT_CARD, "--dj-field": "rgba(255,255,255,0.72)", "--dj-groove": "rgba(10,60,48,0.35)",
    "--dj-edge": "#3d7a6a", "--dj-ink": "#0f342b", "--dj-dim": "#2d5c4f",
    "--dj-knob-hi": "#ffffff", "--dj-knob-1": "#f2fbf7", "--dj-knob-2": "#a9d4c7", "--dj-knob-rim": "#2f6b5b",
    "--dj-sw": "radial-gradient(ellipse 60% 40% at 38% 22%, rgba(255,255,255,0.45), rgba(255,255,255,0) 100%), linear-gradient(180deg, #3f8c77 0%, #2f6f5e 55%, #245a4b 100%)",
    "--dj-sw-ink": "#eafff7", "--dj-sw-rim": "#1b4a3e", "--dj-lit": "#0fa37f",
  },
  cherry: {
    "--dj-base": "radial-gradient(120% 70% at 20% 0%, rgba(255,150,150,0.35) 0%, rgba(255,150,150,0) 55%), linear-gradient(160deg, #a21f33 0%, #7a1020 45%, #46060f 100%)",
    "--dj-card": "rgba(30,0,6,0.38)", "--dj-field": "rgba(30,0,6,0.45)", "--dj-groove": "rgba(25,0,4,0.6)",
    "--dj-edge": "#220207", "--dj-ink": "#ffeef0", "--dj-dim": "#f1b9c0",
    "--dj-knob-hi": "#fff4e2", "--dj-knob-1": "#f3d9b3", "--dj-knob-2": "#a17a4c", "--dj-knob-rim": "#220207",
    "--dj-sw": "radial-gradient(ellipse 60% 40% at 38% 22%, rgba(255,200,200,0.35), rgba(255,200,200,0) 100%), linear-gradient(180deg, #4a0b15 0%, #2f0610 55%, #1c030a 100%)",
    "--dj-sw-ink": "#ffe3e6", "--dj-sw-rim": "#12020a", "--dj-lit": "#ff6178",
  },
  space: {
    "--dj-base": "radial-gradient(90% 60% at 80% 0%, rgba(53,224,255,0.20) 0%, rgba(53,224,255,0) 60%), linear-gradient(160deg, #1c2c57 0%, #121d3d 50%, #090f22 100%)",
    "--dj-card": "rgba(3,8,24,0.45)", "--dj-field": "rgba(3,8,24,0.55)", "--dj-groove": "rgba(0,4,16,0.7)",
    "--dj-edge": "#35e0ff", "--dj-ink": "#dcefff", "--dj-dim": "#8fb4d8",
    "--dj-knob-hi": "#ffffff", "--dj-knob-1": "#dbe9fb", "--dj-knob-2": "#6f87ab", "--dj-knob-rim": "#06102a",
    "--dj-sw": "radial-gradient(ellipse 60% 40% at 38% 22%, rgba(120,230,255,0.28), rgba(120,230,255,0) 100%), linear-gradient(180deg, #1b2a55 0%, #101a38 55%, #0a1126 100%)",
    "--dj-sw-ink": "#8ff3ff", "--dj-sw-rim": "#35e0ff", "--dj-lit": "#35e0ff",
  },
  gold: {
    "--dj-base": "repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0 1px, rgba(0,0,0,0.05) 1px 2px), linear-gradient(135deg, #e8bf3d 0%, #c99a1c 24%, #9a6f0b 52%, #d9aa2a 74%, #7a5506 100%)",
    "--dj-card": "rgba(70,40,0,0.30)", "--dj-field": "rgba(60,32,0,0.38)", "--dj-groove": "rgba(50,28,0,0.6)",
    "--dj-edge": "#1a1204", "--dj-ink": "#fff6dc", "--dj-dim": "#fbe6a6",
    "--dj-knob-hi": "#fffbe0", "--dj-knob-1": "#ffe680", "--dj-knob-2": "#b07d0a", "--dj-knob-rim": "#2b1d00",
    "--dj-sw": "radial-gradient(ellipse 60% 40% at 38% 22%, rgba(255,251,224,0.85), rgba(255,251,224,0) 100%), radial-gradient(circle at 36% 28%, #ffe680 0%, #f7c928 40%, #d9a514 70%, #8f6205 100%)",
    "--dj-sw-ink": "#2b1d00", "--dj-sw-rim": "#1a1204", "--dj-lit": "#fff2a8",
    // the green and amber words ("Screen connected") must show on a bright gold ground
    "--dj-ok": "#0b4a34", "--dj-warn": "#4a2a00",
  },
  quartz: {
    "--dj-base": "linear-gradient(rgba(255,255,255,0.22), rgba(255,255,255,0.06)), var(--dj-slab, linear-gradient(160deg, #fbf9f3, #ebe5d6))",
    "--dj-card": "rgba(255,255,255,0.55)", "--dj-field": "rgba(255,255,255,0.8)", "--dj-groove": "rgba(90,70,30,0.3)",
    "--dj-edge": "#c79a2e", "--dj-ink": "#2b2418", "--dj-dim": "#5a4d36",
    "--dj-knob-hi": "#fffbe0", "--dj-knob-1": "#f3cd62", "--dj-knob-2": "#a97c10", "--dj-knob-rim": "#4d3505",
    "--dj-sw": "radial-gradient(ellipse 60% 40% at 38% 22%, rgba(255,255,255,0.9), rgba(255,255,255,0) 100%), linear-gradient(180deg, #ffffff 0%, #f4efe4 55%, #dcd4c2 100%)",
    "--dj-sw-ink": "#2b2418", "--dj-sw-rim": "#b88a1e", "--dj-lit": "#c79a2e",
  },
};

// the "connected" green and the "waiting" amber, readable on a dark base and on a light one
const OK_ON_DARK = { "--dj-ok": "#34d399", "--dj-warn": "#fbbf24" };
const OK_ON_LIGHT = { "--dj-ok": "#0a7a5c", "--dj-warn": "#8a5200" };

export function mineVars(m) {
  const lightBase = isLight(m.base);
  return {
    "--dj-base": `linear-gradient(160deg, ${mix(m.base, "#ffffff", 0.18)} 0%, ${m.base} 45%, ${mix(m.base, "#000000", 0.3)} 100%)`,
    "--dj-card": lightBase ? LIGHT_CARD : DARK_CARD,
    "--dj-field": lightBase ? "rgba(255,255,255,0.72)" : "rgba(0,0,0,0.35)",
    "--dj-groove": lightBase ? "rgba(0,0,0,0.28)" : "rgba(0,0,0,0.55)",
    "--dj-edge": mix(m.base, "#000000", 0.5),
    "--dj-ink": lightBase ? "#14181d" : "#f1f4f7",
    "--dj-dim": lightBase ? "#37404c" : "#b6c0cc",
    "--dj-knob-hi": mix(m.knob, "#ffffff", 0.65), "--dj-knob-1": m.knob, "--dj-knob-2": mix(m.knob, "#000000", 0.35), "--dj-knob-rim": mix(m.knob, "#000000", 0.6),
    "--dj-sw": `radial-gradient(ellipse 60% 40% at 38% 22%, rgba(255,255,255,0.4), rgba(255,255,255,0) 100%), linear-gradient(180deg, ${mix(m.sw, "#ffffff", 0.22)} 0%, ${m.sw} 55%, ${mix(m.sw, "#000000", 0.2)} 100%)`,
    "--dj-sw-ink": isLight(m.sw) ? "#101418" : "#f5f7fa",
    "--dj-sw-rim": mix(m.sw, "#000000", 0.55),
    "--dj-lit": m.lit, "--dj-lights": m.lit,
  };
}

// The hook the page uses: the chosen look, the colors for "My colors", setters that also keep them on the device, and the
// CSS variables to put on the controller. The wood grain (drawn once with the canvas) and the quartz slab (a photo, loaded
// only when used) are made when their look is picked.
export function useDjLook() {
  const [look, setLookState] = useState(readLook);
  const [mine, setMineState] = useState(readMine);
  const [extra, setExtra] = useState({});

  const setLook = useCallback((k) => { setLookState(k); keep(LOOK_KEY, k === "steel" ? null : k); }, []);
  const setMine = useCallback((patch) => {
    setMineState((prev) => {
      const next = { ...prev, ...patch };
      keep(MINE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);
  const resetMine = useCallback(() => { setMineState({ ...MINE_DEFAULT }); keep(MINE_KEY, null); }, []);

  useEffect(() => {
    if (look === "wood") {
      setExtra((e) => {
        if (e["--dj-wood"]) return e;
        try { return { ...e, "--dj-wood": `url("${plywoodDataUrl(720, 520, 7)}")` }; } catch { return e; }
      });
      return undefined;
    }
    if (look === "quartz") {
      let live = true;
      loadSlab().then((slab) => { if (live && slab) setExtra((e) => ({ ...e, "--dj-slab": slab.box })); });
      return () => { live = false; };
    }
    return undefined;
  }, [look]);

  const vars = useMemo(() => {
    const base = look === "mine" ? mineVars(mine) : PRESETS[look] || PRESETS.steel;
    const light = look === "mine" ? isLight(mine.base) : look === "mint" || look === "quartz";
    return { ...(light ? OK_ON_LIGHT : OK_ON_DARK), ...base, ...extra };
  }, [look, mine, extra]);
  return { look, setLook, mine, setMine, resetMine, vars };
}
